import { css, cx } from "@emotion/css";
import { borderRadius, borderWidth, colorScales, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
interface CardProps {
    className?: string;
    logo: React.ReactNode;
    header: React.ReactNode;
    content: React.ReactNode;
    onHoverView?: React.ReactNode;
    link?: React.ReactNode;
    isDisabled?: boolean;
    leftAlign?: boolean;
    contentClassName?: string;
    disableMargin?: boolean;
    onClick?: () => void;
}
const Card: React.FC<CardProps> = (props: CardProps) => {
    return (<div className={cx(styles.container, {
            [styles.disabled]: props.isDisabled,
            [styles.cardLeft]: props.leftAlign,
            [styles.containerMargin]: props.disableMargin !== true,
        }, props.className)} onClick={props.onClick}>
            {props.link && <div className={styles.link}>{props.link}</div>}
            <div className={props.leftAlign ? styles.contentLeft : undefined}>{props.logo}</div>
            <div className={!props.logo ? styles.headerNoLogo : cx(styles.header, { [styles.headerLeft]: props.leftAlign })}>{props.header}</div>
            <div className={cx(styles.content, { [styles.contentLeft]: props.leftAlign }, props.contentClassName)}>{props.content}</div>
            {props.onHoverView && <div className={cx(styles.hoverView, hoverViewClassName)}>{props.onHoverView}</div>}
        </div>);
};
Card.displayName = "Card"
const hoverViewClassName = "hoverView";
const cardWidth = "12.75rem";
const cardPadding = space[16];
const styles = {
    container: css({
        width: `calc(${cardWidth} - ${cardPadding})`,
        padding: `${cardPadding} ${cardPadding} 0 ${cardPadding}`,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flex: "0 1 auto",
        boxShadow: themeTokens.shadow.small,
        borderRadius: borderRadius.large,
        overflow: "hidden",
        color: themeTokens.color.text.primary,
        textDecoration: "none",
        backgroundColor: themeTokens.color.portal.card.background,
        transitionProperty: "background-color, box-shadow",
        transitionDuration: "0.1s",
        transitionTimingFunction: "ease-in",
        "&:active, &:visited": {
            color: themeTokens.color.text.primary,
            textDecoration: "none",
        },
        "&:hover, &:focus-within": {
            boxShadow: themeTokens.shadow.large,
            color: themeTokens.color.text.primary,
            [`.${hoverViewClassName}`]: {
                opacity: 1,
                pointerEvents: "auto",
            },
        },
        "&:focus-within": {
            border: `${borderWidth[1]} solid ${colorScales.white}`,
        },
        /* Force clean hover state reset - this ensures stuck hover states are cleared */
        ":not(:hover)": {
            /* Reset container hover styles */
            boxShadow: themeTokens.shadow.small,
            backgroundColor: themeTokens.color.portal.card.background,
            [`.${hoverViewClassName}`]: {
                opacity: 0,
                pointerEvents: "none",
            },
        },
        "@media (max-width: 37.4rem)": {
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "stretch",
            padding: `calc(${cardPadding} - 0.5rem)`,
            height: "auto",
            width: "100%",
        },
    }),
    containerMargin: css({
        margin: "0.8rem",
        "@media (max-width: 37.4rem)": {
            margin: "0.8rem 0",
        },
    }),
    hoverView: css({
        position: "absolute",
        flexDirection: "column",
        bottom: 0,
        left: 0,
        height: "100%",
        width: "100%",
        justifyContent: "center",
        alignItems: "center",
        opacity: 0,
        transition: "opacity 0.1s ease-in",
        /* Ensure clean hover state transitions */
        pointerEvents: "none",
    }),
    header: css({
        maxWidth: `calc(${cardWidth} - ${cardPadding} * 0.5)`,
        fontSize: "1.0625rem",
        marginTop: space[16],
        textAlign: "center",
        color: themeTokens.color.text.primary,
        fontWeight: 400,
        wordWrap: "break-word",
        wordBreak: "break-word",
        "@media (max-width: 37.4rem)": {
            marginTop: "auto",
            marginBottom: "auto",
            maxWidth: "none",
            flex: "1 1 auto",
            textAlign: "left",
        },
    }),
    headerNoLogo: css({
        marginTop: 0,
    }),
    link: css({
        "& > a": {
            position: "absolute",
            display: "flex",
            flexDirection: "column",
            bottom: 0,
            left: 0,
            height: "100%",
            width: "100%",
            zIndex: 1,
        },
    }),
    disabled: css({
        "& *": {
            color: themeTokens.color.portal.card.content.disabled,
        },
    }),
    content: css({
        textAlign: "center",
        maxWidth: `calc(${cardWidth} - ${cardPadding} * 0.5)`,
        color: themeTokens.color.portal.card.content.text,
        fontSize: "0.8125rem",
        fontWeight: 300,
        lineHeight: "1rem",
        maxHeight: "4.5rem",
        marginBottom: space[16],
        height: "100%",
        //This will all need to go/change when real markdown content is added
        position: "relative",
        overflow: "hidden",
        textOverflow: "ellipsis",
        wordWrap: "break-word",
        p: {
            margin: `${space[8]} 0`, // This helps us get descriptions that line up nicely with 4 lines of text.
        },
        "@media (max-width: 37.4rem)": {
            display: "none",
        },
    }),
    cardLeft: css({
        minWidth: "20rem",
        maxWidth: "30rem",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignContent: "flex-start",
    }),
    headerLeft: css({
        marginTop: "9px",
        textAlign: "left",
        width: "100%",
        alignSelf: "flex-start",
        maxWidth: "100%",
    }),
    contentLeft: css({
        textAlign: "left",
        width: "100%",
        alignSelf: "flex-start",
        maxWidth: "100%",
        marginTop: 0,
    }),
};
export default Card;
